import { IconPositions } from "@/baseComponents";
import { Button } from "@/components/atoms/button";
import { Chip } from "@/components/atoms/chip";
import { MediaImage } from "@/components/atoms/media";
import { FundsHeaderBaseProps } from "@/components/organisms/fundsDetailHeader";
import { ChipVariants, FundsTagType } from "@/types/consts";
import { ContactLinkTypes, MediaTypes } from "@/types/enums";
import { formatContactUrl } from "@/utils/contactUrlFormatter";
import { buildImageUrl } from "@/utils/imageSharpUrlBuilder";
import { Box, chakra, Heading, Text } from "@chakra-ui/react";
import DOMPurify from "isomorphic-dompurify";
import { useTranslations } from "next-intl";
import { MdOutlineAlternateEmail } from "react-icons/md";
import { RxMobile } from "react-icons/rx";

export default chakra(HeaderSidebar);
function HeaderSidebar({
  title,
  openForInvestment,
  tags,
  contactTitle,
  contactSubtitle,
  contactDetails,
}: FundsHeaderBaseProps) {
  const t = useTranslations();

  return (
    <Box>
      <Box
        display="flex"
        flexDirection="column"
        gap={5}
        p={6}
        rounded="1rem"
        bgColor="brand.grey"
      >
        <Chip
          ellipse
          ellipseColor={!openForInvestment ? "brand.aubergine" : undefined}
          text={
            openForInvestment ? t("investment Open") : t("investment Closed")
          }
          variant={ChipVariants.Solid}
          w="fit-content"
        />
        <Heading
          as="h1"
          size="unset"
          textStyle="display.xs"
          fontWeight={400}
          color={"brand.aubergine"}
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(title.replace(/<\/p>/g, ""), {
              ALLOWED_TAGS: ["strong"],
            }),
          }}
          sx={{
            strong: {
              display: "block",
              pt: 2,
              textStyle: "heading.xl",
              fontWeight: 300,
              color: "brand.cyan",
            },
          }}
        />
        <Box display="flex" flexWrap="wrap" gap={2}>
          {tags?.map((tag) => (
            <Chip
              key={tag.type}
              variant={ChipVariants.Outline}
              text={
                tag.type === FundsTagType.MinimumInvestment
                  ? `${t("minimum Investment Label")}: ${tag.value}`
                  : tag.value
              }
            />
          ))}
        </Box>
      </Box>
      <Box
        display={{ base: "none", lg: "block" }}
        p={6}
        rounded="1rem"
        bgColor="brand.grey"
      >
        <Heading
          size="unset"
          textStyle="display.xxs"
          fontWeight={400}
          color="brand.aubergine"
        >
          {contactTitle ?? t("funds Contact Title")}
        </Heading>
        <Text textStyle="heading.xs" fontWeight={300} color="aubergine.950">
          {contactSubtitle ?? t("funds Contact SubTitle")}
        </Text>
        <Box display="flex" gap={5} mt={8}>
          {contactDetails?.mediaData &&
            contactDetails.mediaData.type === MediaTypes.Image && (
              <Box w={20} h={20} minW={20} flexShrink={0}>
                <MediaImage
                  w="full"
                  h="full"
                  rounded="full"
                  viewportWidthFactor={{ base: 0.5 }}
                  cropSizeAlias={{ base: "square" }}
                  imageUrlBuilder={buildImageUrl}
                  {...contactDetails.mediaData}
                />
              </Box>
            )}
          <Box color="aubergine.950">
            <Text
              textStyle={{ base: "heading.sm", xl: "heading.md" }}
              fontWeight={300}
            >
              {contactDetails?.firstName} {contactDetails?.lastName}
            </Text>
            <Text textStyle={{ base: "paragraph.sm", xl: "paragraph.lg" }}>
              {contactDetails?.jobTitle}
            </Text>
          </Box>
        </Box>
        <Box display="flex" gap={2} w="full" mt={5}>
          {contactDetails?.email && (
            <Button
              text={t("funds Contact Message")}
              url={formatContactUrl(
                ContactLinkTypes.Email,
                contactDetails.email,
              )}
              icon={<MdOutlineAlternateEmail />}
              iconPosition={IconPositions.Left}
              buttonProps={{ flex: 1, px: 4, py: 2, h: "fit-content" }}
            />
          )}
          {contactDetails?.phone && (
            <Button
              text={t("funds Contact Call")}
              url={formatContactUrl(
                ContactLinkTypes.Phone,
                contactDetails.phone,
              )}
              icon={<RxMobile />}
              iconPosition={IconPositions.Left}
              buttonProps={{ flex: 1, px: 4, py: 2, h: "fit-content" }}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
}
